import {
  focusPlayfieldCanvas,
  releaseDomOverlayFocus,
  setPlayerOverlayOpen,
} from "./player-input-gate.js";

export type OpenZoneTransitConfirmOptions = {
  message: string;
  /** Dialog heading; defaults to "Travel". */
  title?: string;
  onConfirm: () => void;
  onCancel?: () => void;
};

let uiAbort: AbortController | null = null;
let pendingOnConfirm: (() => void) | undefined;
let pendingOnCancel: (() => void) | undefined;

export function isZoneTransitConfirmOpen(): boolean {
  if (typeof document === "undefined") return false;
  return (
    document.getElementById("zone-transit-confirm-overlay")?.classList.contains("open") ===
    true
  );
}

export function closeZoneTransitConfirm(): void {
  uiAbort?.abort();
  uiAbort = null;
  pendingOnConfirm = undefined;
  pendingOnCancel = undefined;

  const overlay = document.getElementById("zone-transit-confirm-overlay");
  if (overlay) {
    releaseDomOverlayFocus(overlay);
    overlay.classList.remove("open");
    overlay.setAttribute("aria-hidden", "true");
  }

  setPlayerOverlayOpen("zone-transit-confirm", false);
  focusPlayfieldCanvas();
}

export function openZoneTransitConfirm(opts: OpenZoneTransitConfirmOptions): void {
  if (isZoneTransitConfirmOpen()) return;

  const overlay = document.getElementById("zone-transit-confirm-overlay");
  const titleEl = document.getElementById("zone-transit-confirm-title");
  const bodyEl = document.getElementById("zone-transit-confirm-body");
  const yesBtn = document.getElementById("zone-transit-confirm-yes") as HTMLButtonElement | null;
  const noBtn = document.getElementById("zone-transit-confirm-no") as HTMLButtonElement | null;
  if (!overlay || !titleEl || !bodyEl || !yesBtn || !noBtn) return;

  pendingOnConfirm = opts.onConfirm;
  pendingOnCancel = opts.onCancel;

  titleEl.textContent = opts.title?.trim() || "Travel";
  bodyEl.textContent = opts.message;
  overlay.classList.add("open");
  overlay.setAttribute("aria-hidden", "false");
  setPlayerOverlayOpen("zone-transit-confirm", true);

  uiAbort = new AbortController();
  const { signal } = uiAbort;

  const dismiss = (confirmed: boolean) => {
    const onConfirm = pendingOnConfirm;
    const onCancel = pendingOnCancel;
    closeZoneTransitConfirm();
    if (confirmed) onConfirm?.();
    else onCancel?.();
  };

  yesBtn.addEventListener("click", () => dismiss(true), { signal });
  noBtn.addEventListener("click", () => dismiss(false), { signal });

  overlay.addEventListener(
    "click",
    (ev) => {
      if (ev.target === overlay) dismiss(false);
    },
    { signal }
  );

  document.addEventListener(
    "keydown",
    (ev) => {
      if (ev.key === "Escape") {
        ev.preventDefault();
        dismiss(false);
      }
    },
    { signal }
  );

  queueMicrotask(() => yesBtn.focus());
}
